import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTicketsSearch, useTicketsData, useSelectedTickets, useProjectData } from '../ProjectsPage/ProjectLayout';
import { useJiraSpaces } from '../../hooks/useJiraSpaces';
import { ExpandableSearch, ActionAlert, SyncingBadge, SkeletonTable } from '../../components/atoms';
import {
  IonButton,
  IonIcon,
  IonSelect,
  IonSelectOption,
  IonProgressBar
} from '@ionic/react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  syncOutline, 
  alertCircleOutline,
  ticketOutline,
  chevronDown,
  chevronForward,
  closeCircleOutline
} from 'ionicons/icons';
import api from '../../services/api';
import './TicketsPage.css';

// Get Jira-style color for issue types
const getIssueTypeColor = (issueTypeName) => {
  const name = (issueTypeName || '').toLowerCase();
  if (name.includes('story')) return '#4bce97'; // Jira green
  if (name.includes('bug')) return '#f87168'; // Jira red
  if (name.includes('task')) return '#579dff'; // Jira blue
  if (name.includes('epic')) return '#9f8fef'; // Jira purple
  if (name.includes('subtask')) return '#6b778c'; // Jira grey
  if (name.includes('feature')) return '#4bce97'; // Jira green
  // Default grey
  return '#6b778c';
};

const TicketsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { searchQuery, setSearchQuery } = useTicketsSearch();
  // Use shared tickets context - single source of truth
  const { 
    tickets, 
    setTickets,
    loadTickets, 
    ticketsLoading: loading, 
    ticketsSyncing: syncing, 
    ticketsError: error
  } = useTicketsData();
  const { selectedTickets, setSelectedTickets } = useSelectedTickets();
  const [filterStatus, setFilterStatus] = useState('all');
  const [collapsedSprints, setCollapsedSprints] = useState({});
  
  // Get project data from context (avoids duplicate fetch)
  const { project } = useProjectData();
  
  // Jira spaces management
  const { jiraSpaces, selectedSpace, loadingSpaces, handleSpaceChange } = useJiraSpaces(projectId, project);
  
  // Drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  );
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    // Find sprint group
    let sprintGroup = null;
    for (const group of sortedGroups) {
      if (group.tickets.some(t => t.key === active.id)) {
        sprintGroup = group;
        break;
      }
    }
    if (!sprintGroup) return;
    
    const sprintTickets = sprintGroup.tickets;
    const oldIdx = sprintTickets.findIndex(t => t.key === active.id);
    const overIdx = sprintTickets.findIndex(t => t.key === over.id);
    if (oldIdx === -1 || overIdx === -1) return;
    
    // Use arrayMove from dnd-kit
    const reordered = arrayMove(sprintTickets, oldIdx, overIdx);
    const newIdx = reordered.findIndex(t => t.key === active.id);
    
    // Use customOrder if set, otherwise the ticket's current position in the sorted list
    const getOrder = (ticket, idx) => ticket.customOrder !== undefined ? ticket.customOrder : (ticket._implicitOrder !== undefined ? ticket._implicitOrder : idx);
    
    // Calculate new order based on neighbors
    let newOrder;
    if (newIdx === 0) {
      const nextOrder = getOrder(reordered[1], 1);
      newOrder = nextOrder - 1;
    } else if (newIdx === reordered.length - 1) {
      const prevOrder = getOrder(reordered[newIdx - 1], newIdx - 1);
      newOrder = prevOrder + 1;
    } else {
      const prevOrder = getOrder(reordered[newIdx - 1], newIdx - 1);
      const nextOrder = getOrder(reordered[newIdx + 1], newIdx + 1);
      newOrder = (prevOrder + nextOrder) / 2;
    }
    
    // Update state
    setTickets(tickets.map(t => t.key === active.id ? {...t, customOrder: newOrder} : t));
    
    // Save to backend
    try {
      await api.patch(`/projects/${projectId}/tickets/${active.id}/order`, { customOrder: newOrder });
    } catch (error) {
      console.error('Error:', error);
      setTickets(tickets);
    }
  };
  
  // Analysis polling state
  const [analyses, setAnalyses] = useState({});
  const [, setPollingActive] = useState(false);
  const pollingIntervalRef = useRef(null);

  // ProjectLayout already loads tickets when navigating to this page, no need to duplicate
  
  // Poll for latest analyses (all statuses: running, completed, failed, etc.)
  const pollAnalyses = React.useCallback(async () => {
    if (!projectId) return;
    
    try {
      // No ticketKeys needed - backend returns latest analysis for each ticket in project
      const data = await api.get(
        `/projects/${projectId}/tickets/analyses/latest`
      );
      
      // Convert array to map for easy lookup
      const analysesMap = {};
      (data.analyses || []).forEach(analysis => {
        analysesMap[analysis.ticketKey] = analysis;
      });
      
      setAnalyses(analysesMap);
      
      // NEVER stop polling - always continue
      // This ensures progress bars update in real-time for running analyses
      // and user can start new analyses without page refresh
    } catch (error) {
      console.error('Error polling analyses:', error);
      // Continue polling even on error
    }
  }, [projectId]);
  
  // Start polling when component mounts (always runs, never stops)
  useEffect(() => {
    if (!projectId) return;
    
    // Initial fetch
    pollAnalyses();
    
    // Set up polling interval (every 5 seconds) - always runs
    setPollingActive(true);
    pollingIntervalRef.current = setInterval(pollAnalyses, 5000);
    
    // Cleanup only on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setPollingActive(false);
      }
    };
  }, [projectId, pollAnalyses]);
  
  // Get analysis for a specific ticket
  const getTicketAnalysis = (ticketKey) => {
    return analyses[ticketKey] || null;
  };


  // Handle drag end

  // Get unique statuses from tickets for the dropdown
  const uniqueStatuses = [...new Set(tickets.map(t => t.status))].sort();

  const filteredTickets = tickets.filter(ticket => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const displaySummary = ticket.localOverrides?.summary || ticket.summary;
      if (!ticket.key.toLowerCase().includes(query) &&
          !displaySummary.toLowerCase().includes(query)) {
        return false;
      }
    }

    // Status filter - filter by actual status name
    if (filterStatus !== 'all') {
      if (ticket.status !== filterStatus) {
        return false;
      }
    }

    return true;
  });

  // Group tickets by sprint FIRST (don't sort yet)
  const groupedTickets = filteredTickets.reduce((groups, ticket) => {
    const sprintName = ticket.sprint?.name || 'Backlog';
    const sprintState = ticket.sprint?.state || 'backlog';
    
    if (!groups[sprintName]) {
      groups[sprintName] = {
        name: sprintName,
        state: sprintState,
        tickets: []
      };
    }
    groups[sprintName].tickets.push(ticket);
    return groups;
  }, {});

  // Sort tickets within each group by customOrder (if set), else preserve original order by ticket number.
  // Assign implicit indices so dragged tickets (which gain customOrder) stay comparable with the rest.
  Object.values(groupedTickets).forEach(group => {
    group.tickets.sort((a, b) => {
      const aNum = parseInt(a.key?.split('-').pop(), 10) || 0;
      const bNum = parseInt(b.key?.split('-').pop(), 10) || 0;
      return aNum - bNum;
    });
    group.tickets.forEach((t, i) => {
      if (t.customOrder === undefined) t._implicitOrder = i;
    });
    group.tickets.sort((a, b) => {
      const aOrder = a.customOrder !== undefined ? a.customOrder : a._implicitOrder;
      const bOrder = b.customOrder !== undefined ? b.customOrder : b._implicitOrder;
      return aOrder - bOrder;
    });
  });

  // Sort groups: active sprints first, then future, then backlog, then closed (descending)
  const sprintStateOrder = { active: 0, future: 1, backlog: 2, closed: 3 };
  const sortedGroups = Object.values(groupedTickets).sort((a, b) => {
    const aOrder = sprintStateOrder[a.state] ?? 2;
    const bOrder = sprintStateOrder[b.state] ?? 2;
    if (aOrder !== bOrder) return aOrder - bOrder;

    // Within the same state, sort by sprint number descending (newest first)
    const aNum = parseInt((a.name.match(/(\d+)\s*$/) || [])[1], 10) || 0;
    const bNum = parseInt((b.name.match(/(\d+)\s*$/) || [])[1], 10) || 0;
    if (aNum !== bNum) return bNum - aNum;

    return a.name.localeCompare(b.name);
  });

  // Auto-collapse all sprints except the latest one
  useEffect(() => {
    if (sortedGroups.length > 0) {
      const latestSprint = sortedGroups[0].name;
      const initialCollapsed = {};
      sortedGroups.forEach(group => {
        // Collapse everything except the latest sprint (first one)
        initialCollapsed[group.name] = group.name !== latestSprint;
      });
      setCollapsedSprints(initialCollapsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets.length]); // Re-run when tickets change

  const toggleSprintCollapse = (sprintName) => {
    setCollapsedSprints(prev => ({
      ...prev,
      [sprintName]: !prev[sprintName]
    }));
  };

  const handleSelectTicket = (ticketKey, checked) => {
    setSelectedTickets(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(ticketKey);
      } else {
        newSet.delete(ticketKey);
      }
      return newSet;
    });
  };

  const handleTicketClick = (e, ticketKey) => {
    // Don't navigate if clicking on checkbox
    if (e.target.type === 'checkbox') {
      return;
    }
    navigate(`/projects/${projectId}/tickets/${ticketKey}`);
  };

  const clearSelection = () => {
    setSelectedTickets(new Set());
  };

  if (error) {
    const isIntegrationError = error.includes('not connected') || error.includes('not active');

    if (isIntegrationError) {
      return (
        <div className="tickets-error">
          <ActionAlert
            title="Jira Not Connected"
            description="Connect your Jira account to import tickets."
            actionLabel="Connect"
            actionTo="/integrations"
            fadeIn
          />
        </div>
      );
    }

    return (
      <div className="tickets-error">
        <IonIcon icon={alertCircleOutline} />
        <p>{error}</p>
        <IonButton onClick={() => loadTickets()}>
          Retry
        </IonButton>
      </div>
    );
  }

  if (loading) {
    return <SkeletonTable filterCount={3} overlay={<SyncingBadge label="Syncing" />} />;
  }

  return (
    <>
    <div className="tickets-page-wrapper breadcrumb-spacing">
      <div className="tickets-page">
          {/* Filters */}
          <div className="tickets-filters">
            <div className="status-filters">
                  {/* Jira Space Selector */}
                  {jiraSpaces.length > 0 && selectedSpace && (
                    <IonSelect
                      value={selectedSpace}
                      onIonChange={(e) => handleSpaceChange(e.detail.value, () => loadTickets(true, e.detail.value))}
                      interface="popover"
                      className="status-dropdown"
                      placeholder="Select Space"
                      disabled={loadingSpaces || syncing}
                    >
                      {jiraSpaces.map(space => (
                        <IonSelectOption key={space.key} value={space.key}>
                          {space.key} - {space.name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  )}
                  
                  {/* Status Filter */}
                  <IonSelect
                    value={filterStatus}
                    onIonChange={(e) => setFilterStatus(e.detail.value)}
                    interface="popover"
                    className="status-dropdown"
                    placeholder="Status"
                  >
                    <IonSelectOption value="all">
                      All ({tickets.length})
                    </IonSelectOption>
                    {uniqueStatuses.map(status => (
                      <IonSelectOption key={status} value={status}>
                        {status} ({tickets.filter(t => t.status === status).length})
                      </IonSelectOption>
                    ))}
                  </IonSelect>

                  {selectedTickets.size > 0 && (
                    <>
                      <span className="selection-count">
                        {selectedTickets.size} selected
                      </span>
                      <button 
                        className="sync-button"
                        onClick={clearSelection}
                        title="Clear selection"
                      >
                        <IonIcon icon={closeCircleOutline} />
                      </button>
                    </>
                  )}
                  
                  {selectedTickets.size === 0 && (
                    <button 
                      className={`sync-button ${syncing ? 'syncing' : ''}`}
                      onClick={() => loadTickets(true)}
                      disabled={syncing}
                      title="Sync with Jira"
                    >
                      <IonIcon icon={syncOutline} />
                    </button>
                  )}
            </div>
            <div className="tickets-filters-right">
              <ExpandableSearch
                placeholder="Search tickets..."
                value={searchQuery || ''}
                onChange={(val) => setSearchQuery(val)}
                debounce={300}
              />
            </div>
          </div>

          {/* Tickets List - Grouped by Sprint */}
          {filteredTickets.length === 0 ? (
            <div className="tickets-empty">
              <IonIcon icon={alertCircleOutline} />
              <h2>No tickets found</h2>
              <p>
                {searchQuery || filterStatus !== 'all'
                  ? 'Try adjusting your filters'
                  : 'No Jira tickets found for this project'}
              </p>
            </div>
          ) : (
            <div className="tickets-grouped">
              {sortedGroups.map((group) => {
                const isCollapsed = collapsedSprints[group.name];
                return (
                  <div key={group.name} className={`sprint-group ${isCollapsed ? 'collapsed' : ''}`}>
                    <div 
                      className={`sprint-header sprint-${group.state}`}
                    >
                    <input 
                      type="checkbox"
                      className="sprint-checkbox"
                      checked={group.tickets.length > 0 && group.tickets.every(t => selectedTickets.has(t.key))}
                      onChange={(e) => {
                        e.stopPropagation();
                        const checked = e.target.checked;
                        setSelectedTickets(prev => {
                          const newSet = new Set(prev);
                          group.tickets.forEach(ticket => {
                            if (checked) {
                              newSet.add(ticket.key);
                            } else {
                              newSet.delete(ticket.key);
                            }
                          });
                          return newSet;
                        });
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <IonIcon 
                      icon={isCollapsed ? chevronForward : chevronDown} 
                      className="sprint-toggle"
                      onClick={() => toggleSprintCollapse(group.name)}
                    />
                      <span className="sprint-name" onClick={() => toggleSprintCollapse(group.name)}>{group.name}</span>
                      <span className="sprint-count" onClick={() => toggleSprintCollapse(group.name)}>{group.tickets.length} issues</span>
                    </div>
                    {!isCollapsed && (
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <table className="tickets-table">
                        <tbody>
                          <SortableContext items={group.tickets.map(t => t.key)} strategy={verticalListSortingStrategy}>
                            {group.tickets.map((ticket) => {
                              const analysis = getTicketAnalysis(ticket.key);
                              const hasAnalysis = analysis && analysis.status;
                              const isAnalyzing = hasAnalysis && ['pending', 'running'].includes(analysis.status);
                              
                              return (
                                <SortableRow 
                                  key={ticket.key} 
                                  ticket={ticket}
                                  analysis={analysis}
                                  hasAnalysis={hasAnalysis}
                                  isAnalyzing={isAnalyzing}
                                  selectedTickets={selectedTickets}
                                  handleSelectTicket={handleSelectTicket}
                                  handleTicketClick={handleTicketClick}
                                  getTicketAnalysis={getTicketAnalysis}
                                  getIssueTypeColor={getIssueTypeColor}
                                  projectId={projectId}
                                />
                              );
                            })}
                          </SortableContext>
                        </tbody>
                      </table>
                      </DndContext>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

function SortableRow({ ticket, analysis, hasAnalysis, isAnalyzing, selectedTickets, handleSelectTicket, handleTicketClick, getTicketAnalysis, getIssueTypeColor, projectId }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: ticket.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <tr ref={setNodeRef} style={style} className="ticket-row" data-ticket-key={ticket.key} onClick={(e) => handleTicketClick(e, ticket.key)} {...attributes} {...listeners}>
      <td className="col-checkbox" onClick={(e) => e.stopPropagation()}>
        <input 
          type="checkbox"
          checked={selectedTickets.has(ticket.key)}
          onChange={(e) => handleSelectTicket(ticket.key, e.target.checked)}
        />
      </td>
      <td className="col-type">
        {ticket.issueType?.iconUrl ? (
          <img 
            src={ticket.issueType.iconUrl.replace('size=medium', 'size=xlarge')}
            alt={ticket.issueType.name}
            title={ticket.issueType.name}
            className="issue-type-icon"
          />
        ) : ticket.issueType?.name && (
          <IonIcon 
            icon={ticketOutline}
            className="issue-type-icon"
            style={{ color: getIssueTypeColor(ticket.issueType.name) }}
            title={ticket.issueType.name}
          />
        )}
      </td>
      <td className="col-key">
        <span className="ticket-key-link">{ticket.key}</span>
      </td>
      <td className="col-summary">
        <span className="summary-text">{ticket.localOverrides?.summary || ticket.summary}</span>
      </td>
      <td className="col-story-points">
        {(() => {
          const originalSP = ticket.original_story_point;
          const aiSP = ticket.aiStoryPoints;
          
          const left = originalSP != null ? originalSP : '-';
          const right = aiSP != null ? aiSP : '-';
          
          return (
            <span className="story-points-badge">
              {left} | {right}
            </span>
          );
        })()}
      </td>
      <td className="col-status">
        <span className={`status-badge status-${ticket.statusCategory}`}>
          {ticket.status}
        </span>
      </td>
      <td className="col-analysis">
        {(() => {
          const analysis = getTicketAnalysis(ticket.key);
          if (!analysis) {
            return <span className="no-analysis">—</span>;
          }

          const isRunning = analysis.status === 'running' || analysis.status === 'starting';
          const isBlocked = ['insufficient_context', 'blocked'].includes(analysis.status);

          return (
            <div 
              className="analysis-status" 
              title={`${analysis.status} - ${analysis.progress}%`}
            >
              {(isRunning || analysis.status === 'completed') && (
                <div
                  className={`analysis-progress ${isRunning ? 'running' : ''}`}
                  style={{ '--progress': analysis.progress }}
                >
                  <IonProgressBar
                    value={analysis.progress / 100}
                    className="analysis-progress-bar"
                  />
                </div>
              )}
              {isBlocked && (
                <div className="analysis-progress blocked">
                  <IonProgressBar
                    value={analysis.progress / 100}
                    className="analysis-progress-bar blocked"
                  />
                </div>
              )}
              {analysis.status === 'failed' && (
                <span className="analysis-text">Failed</span>
              )}
            </div>
          );
        })()}
      </td>
    </tr>
  );
}

export default TicketsPage;
